import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, Validators } from '@angular/forms';
import { ModalController, NavParams, ToastController } from '@ionic/angular';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { Order } from 'src/app/models/models';
import { ActivityLog, ActivityLogService, ActivityLogType, UpdatedByFrom } from 'src/app/shared-lib/domains/activity-log';
import { toUserRefDisplayName, UserService } from 'src/app/shared-lib/domains/user';
import { OrderService } from 'src/app/shared/order.service';
import { OrderProcessStatus } from '../../shared-lib/domains/order';

@Component({
  selector: 'app-order-pause-dialog',
  templateUrl: './order-pause-dialog.component.html',
  styleUrls: ['./order-pause-dialog.component.scss'],
  standalone: false
})
export class OrderPauseDialogComponent implements OnInit {
  id: String = null;
  isLoading: boolean = false;

  private title_block = "¡Oh, oh!";
  private title_pending = '¡QUIETO PARAO!';

  private body_pending = `¿Te ha faltado algún documento por añadir? ¿La has liado en alguna configuración? ¡Estás de suerte porque puedes pausar tu pedido!

  Si lo pausas, nos pondremos en contacto contigo en horario de atención al cliente para realizar las modificaciones que necesites.

  Si te has equivocado y quieres cancelar tu pedido pero necesitas tenerlo en casa cuanto antes, puedes pausar este y realizar uno nuevo, nos pondremos en contacto contigo para reembolsarte el pedido pausado.`
    ;
  private body_processing = "Lo sentimos pero ya no puedes pausar tu pedido porque hemos comenzado a procesarlo.";
  private body_other = "Lo sentimos pero este pedido ya no puede ser pausado.";

  // attention = "Si tienes algún problema contacta con atención al cliente.";
  attention = () => `Si tienes algún problema contacta con atención al cliente a través de nuestro chat o escribiendo un correo a <a class="highlight" href="mailto:info@copyfly.es?subject=Ayuda%20Pedido%20#${this.order?.number}" target="_blank">info@copyfly.es</a>.`;

  confirm = 'Quiero pausar mi pedido';
  cancel_pending = 'No estoy seguro';
  cancel_other = 'Cerrar';
  commentTitle = 'Comentarios';
  commentPlaceholder = 'Dinos qué problema tienes...';


  //#region Form & Validation
  payloadPauseForm = this.fb.group({
    comment: new UntypedFormControl('', Validators.required),
  });
  validationMessages = {
    'comment': [
      { type: 'required', message: 'Escribe algún comentario' }
    ]
  };
  //#endregion

  order: Order = null;
  public order$: Observable<Order>;
  private get __order$(): Observable<Order> {
    return this.orderService.get$(this.id)
      .pipe(
        tap(order => {
          this.order = order;
          this.title = this._title(order);
          this.body = this._body(order);
          this.cancel = this._cancel(order);
        })
      );
  }

  title: string = "";
  body: string = "";
  cancel: string = "";

  private _title(order: Order) {
    switch (order.processStatus) {
      case OrderProcessStatus.PENDING: return this.title_pending;
      default: return this.title_block;
    }
  }

  private _cancel(order: Order) {
    switch (order.processStatus) {
      case OrderProcessStatus.PENDING: return this.cancel_pending;
      default: return this.cancel_other;
    }
  }

  private _body(order: Order) {
    switch (order.processStatus) {
      case OrderProcessStatus.PENDING: return this.body_pending;
      case OrderProcessStatus.PROCESSING: return this.body_processing;
      default: return this.body_other;
    }
  }

  constructor(
    private modalCtrl: ModalController,
    private navParams: NavParams,
    private fb: UntypedFormBuilder,
    private activityLogService: ActivityLogService,
    private orderService: OrderService,
    private userService: UserService,
    private toastController: ToastController,
  ) {
    const { id, ...etc } = navParams.data;
    this.id = id;
  }

  ngOnInit(): void {
    this.order$ = this.__order$;
  }

  public async onSubmit() {
    if (this.isLoading || this.payloadPauseForm.invalid || !this.isProcessPending) return;

    const { comment } = this.payloadPauseForm.value;
    let { id, processStatus } = this.order;

    let _activityLog: ActivityLog = {
      ref: { collection: this.orderService.collectionName, id: this.order.id },
      user: toUserRefDisplayName(this.userService.userG),
      type: ActivityLogType.COMMENT,
      from: UpdatedByFrom.APP,
      payload: { comment }
    };

    processStatus = OrderProcessStatus.ONHOLD;

    let promises: Promise<any>[] = [];

    promises.push(this.orderService.upsert({ id, processStatus }));

    // Subimos el activity Log
    promises.push(this.activityLogService.upsertActivityLog(_activityLog));


    this.isLoading = true;
    await Promise.all(promises)
      .catch(() => {
        this.onErrorSubmit('Error desconocido, vuelva a intentarlo');
        this.isLoading = false;
      })
      .finally(() => this.onBack());
  }

  private async onErrorSubmit(message: string) {
    (await this.toastController.create({
      message,
      color: "danger",
      position: 'bottom',
      duration: 1000 * 2.2
    })).present();
  }
  public onBack() { this.modalCtrl.dismiss(); }
  public isDirty(key): boolean { return this.payloadPauseForm.get(key).dirty; }
  public isTouched(key): boolean { return this.payloadPauseForm.get(key).touched; }

  get isNotPaused(): boolean {
    const _isNotPaused = this.order?.processStatus != OrderProcessStatus.ONHOLD;

    //Cerramos si estamos en pausa
    if (!this.isLoading && this.order && !_isNotPaused) {
      this.order = null;
      this.onErrorSubmit('Tu pedido ya está pausado...');
      this.onBack();
    }
    return _isNotPaused;
  }



  get isProcessPending() {
    return !!(this?.order?.processStatus == OrderProcessStatus.PENDING);
  }
}
